const DEFAULT_POLYGON_OPTIONS = {
  fillColor: '#FF0000',
  fillOpacity: 0.2,
  strokeWeight: 3,
  strokeColor: '#FF0000',
  clickable: true,
  editable: true,
  draggable: true,
  zIndex: 1,
};

const DEFAULT_CIRCLE_OPTIONS = {
  strokeColor: '#0000FF',
  strokeOpacity: 0.8,
  strokeWeight: 3,
  fillColor: '#0000FF',
  fillOpacity: 0.2,
  clickable: true,
  editable: true,
  draggable: true,
  zIndex: 1,
};

const addListener = (target, eventName, handler, listeners) => {
  const listener = target.addListener(eventName, handler);
  listeners.push(listener);
  return listener;
};

const clearListeners = listeners => {
  listeners.forEach(listener => listener.remove());
  listeners.length = 0;
};

const createPointNumberIcon = (google, number) => {
  const label = String(number);
  const size = label.length > 1 ? 28 : 24;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="#DC2626" stroke="#FFFFFF" stroke-width="2"/>
      <text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle"
        fill="#FFFFFF" font-family="Arial, sans-serif" font-size="${label.length > 1 ? 11 : 12}" font-weight="700">${label}</text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
};

/**
 * Lightweight replacement for the deprecated google.maps.drawing.DrawingManager.
 * Supports polygon and circle drawing using native Maps overlays.
 */
export function createGoogleMapsShapeDrawer({
  map,
  google,
  polygonOptions = {},
  circleOptions = {},
  onModeChange = () => {},
  onComplete = () => {},
  onProgress = () => {},
}) {
  let mode = null;
  const listeners = [];

  let polygonPoints = [];
  let pointMarkers = [];
  let previewPolygon = null;
  let circleCenter = null;
  let previewCircle = null;

  const mergedPolygonOptions = { ...DEFAULT_POLYGON_OPTIONS, ...polygonOptions };
  const mergedCircleOptions = { ...DEFAULT_CIRCLE_OPTIONS, ...circleOptions };

  const clearPointMarkers = () => {
    pointMarkers.forEach(marker => marker.setMap(null));
    pointMarkers = [];
  };

  const addPointMarker = (latLng, number) => {
    const marker = new google.maps.Marker({
      position: latLng,
      map,
      icon: createPointNumberIcon(google, number),
      clickable: false,
      zIndex: 10,
      title: `Point ${number}`,
    });
    pointMarkers.push(marker);
  };

  const clearPreview = () => {
    if (previewPolygon) {
      previewPolygon.setMap(null);
      previewPolygon = null;
    }
    if (previewCircle) {
      previewCircle.setMap(null);
      previewCircle = null;
    }
    clearPointMarkers();
    polygonPoints = [];
    circleCenter = null;
  };

  const resetModeState = () => {
    clearPreview();
    clearListeners(listeners);
  };

  const notifyProgress = () => {
    onProgress({
      mode,
      pointCount: polygonPoints.length,
      hasCircleCenter: Boolean(circleCenter),
    });
  };

  const finishPolygon = () => {
    if (mode !== 'polygon' || polygonPoints.length < 3) {
      return false;
    }

    if (previewPolygon) {
      previewPolygon.setMap(null);
      previewPolygon = null;
    }
    clearPointMarkers();

    // Snapshot points before setMode clears them
    const pathPoints = [...polygonPoints];

    const polygon = new google.maps.Polygon({
      ...mergedPolygonOptions,
      paths: pathPoints,
      map,
    });

    const coords = polygon
      .getPath()
      .getArray()
      .map(latLng => [latLng.lat(), latLng.lng()]);

    // Exit draw mode without destroying the finished overlay
    polygonPoints = [];
    clearListeners(listeners);
    mode = null;
    onModeChange(mode);
    notifyProgress();

    onComplete({
      type: 'Polygon',
      overlay: polygon,
      coordinates: coords,
    });

    return true;
  };

  const updatePolygonPreview = () => {
    if (previewPolygon) {
      previewPolygon.setMap(null);
      previewPolygon = null;
    }

    if (polygonPoints.length < 2) {
      return;
    }

    // Preview must not be clickable — otherwise it swallows map clicks/dblclicks
    // and the polygon never completes into form state.
    previewPolygon = new google.maps.Polygon({
      ...mergedPolygonOptions,
      paths: polygonPoints,
      editable: false,
      draggable: false,
      clickable: false,
      map,
    });
  };

  const setMode = nextMode => {
    if (mode === nextMode) {
      return;
    }

    resetModeState();
    mode = nextMode;
    onModeChange(mode);
    notifyProgress();

    if (!mode) {
      return;
    }

    if (mode === 'polygon') {
      addListener(
        map,
        'click',
        event => {
          polygonPoints.push(event.latLng);
          addPointMarker(event.latLng, polygonPoints.length);
          updatePolygonPreview();
          notifyProgress();
        },
        listeners
      );

      addListener(
        map,
        'dblclick',
        event => {
          // Prevent map zoom-on-double-click
          if (event?.stop) {
            event.stop();
          }
          finishPolygon();
        },
        listeners
      );
    }

    if (mode === 'circle') {
      addListener(
        map,
        'click',
        event => {
          const clickPoint = event.latLng;

          if (!circleCenter) {
            circleCenter = clickPoint;
            notifyProgress();
            return;
          }

          const radius = google.maps.geometry.spherical.computeDistanceBetween(
            circleCenter,
            clickPoint
          );

          if (previewCircle) {
            previewCircle.setMap(null);
            previewCircle = null;
          }

          const circle = new google.maps.Circle({
            ...mergedCircleOptions,
            map,
            center: circleCenter,
            radius,
          });

          const center = circle.getCenter();
          const circleData = {
            type: 'Circle',
            overlay: circle,
            center: [center.lat(), center.lng()],
            radius: circle.getRadius(),
          };

          circleCenter = null;
          clearListeners(listeners);
          mode = null;
          onModeChange(mode);
          notifyProgress();

          onComplete(circleData);
        },
        listeners
      );

      addListener(
        map,
        'mousemove',
        event => {
          if (!circleCenter) {
            return;
          }

          const radius = google.maps.geometry.spherical.computeDistanceBetween(
            circleCenter,
            event.latLng
          );

          if (previewCircle) {
            previewCircle.setRadius(radius);
            return;
          }

          previewCircle = new google.maps.Circle({
            ...mergedCircleOptions,
            map,
            center: circleCenter,
            radius,
            editable: false,
            draggable: false,
            clickable: false,
          });
        },
        listeners
      );
    }
  };

  const destroy = () => {
    resetModeState();
    mode = null;
  };

  return {
    getMode: () => mode,
    getPolygonPointCount: () => polygonPoints.length,
    setMode,
    finishPolygon,
    destroy,
  };
}
